import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createStarterKit,
  getItems,
  getRarity,
  getStarterKit,
  getStarterKits,
} from './AdminAPI';

const read = (value, camelName, pascalName) => value?.[camelName] ?? value?.[pascalName];

const createEmptyEntry = () => ({
  entryId: '',
  itemId: '',
  rarityId: '',
  itemLevel: 1,
  rollSeed: 0,
  quantity: 1,
  requiredToStart: false,
});

const createDraft = (version) => ({
  version,
  active: true,
  entries: [createEmptyEntry()],
});

const kitVersion = (kit) => Number(read(kit, 'version', 'Version')) || 0;
const kitId = (kit) => read(kit, 'id', 'Id') || '';
const kitEntries = (kit) => read(kit, 'entries', 'Entries') || [];

export default function AdminStarterKits() {
  const [kits, setKits] = useState([]);
  const [items, setItems] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [selectedKit, setSelectedKit] = useState(null);
  const [draft, setDraft] = useState(() => createDraft(1));
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const highestVersion = useMemo(
    () => kits.reduce((highest, kit) => Math.max(highest, kitVersion(kit)), 0),
    [kits],
  );
  const nextVersion = highestVersion + 1;

  const startNewVersion = useCallback((version = nextVersion) => {
    setSelectedKit(null);
    setDraft(createDraft(version));
    setError('');
  }, [nextVersion]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [loadedKits, loadedItems, loadedRarities] = await Promise.all([
          getStarterKits(),
          getItems(),
          getRarity(),
        ]);
        if (cancelled) return;
        const sortedKits = [...(loadedKits || [])]
          .sort((left, right) => kitVersion(right) - kitVersion(left));
        setKits(sortedKits);
        setItems(loadedItems || []);
        setRarities(loadedRarities || []);
        const highest = sortedKits.reduce(
          (value, kit) => Math.max(value, kitVersion(kit)),
          0,
        );
        setDraft(createDraft(highest + 1));
      } catch (loadError) {
        if (!cancelled) {
          setError('Starter Kit versions could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectKit = async (summary) => {
    const id = kitId(summary);
    if (!id) return;
    setDetailLoading(true);
    setError('');
    try {
      const detail = await getStarterKit(id);
      setSelectedKit(detail || summary);
    } catch (loadError) {
      setError('The selected Starter Kit version could not be loaded.');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateEntry = (index, field, rawValue) => {
    setDraft((current) => {
      const entries = [...current.entries];
      const previous = entries[index];
      let value = rawValue;
      if (['itemLevel', 'rollSeed', 'quantity'].includes(field)) {
        const parsed = Number.parseInt(rawValue, 10);
        value = Number.isFinite(parsed) ? parsed : 0;
      }
      if (field === 'requiredToStart') {
        value = Boolean(rawValue);
      }
      const nextEntry = { ...previous, [field]: value };
      if (field === 'itemId') {
        const item = items.find(candidate => (
          (candidate.id ?? candidate.Id) === value
        ));
        nextEntry.rarityId = item?.rarityId ?? item?.RarityId ?? previous.rarityId;
      }
      entries[index] = nextEntry;
      return { ...current, entries };
    });
  };

  const addEntry = () => {
    setDraft((current) => ({
      ...current,
      entries: [...current.entries, createEmptyEntry()],
    }));
  };

  const removeEntry = (index) => {
    setDraft((current) => ({
      ...current,
      entries: current.entries.filter((_, entryIndex) => entryIndex !== index),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setError('');
    const payload = {
      version: Number.parseInt(draft.version, 10),
      active: true,
      entries: draft.entries.map(entry => ({
        entryId: entry.entryId.trim(),
        itemId: entry.itemId.trim(),
        rarityId: entry.rarityId.trim(),
        itemLevel: entry.itemLevel,
        rollSeed: entry.rollSeed,
        quantity: entry.quantity,
        requiredToStart: entry.requiredToStart,
      })),
    };
    if (!Number.isFinite(payload.version) || payload.version < nextVersion) {
      setError(`Version must be ${nextVersion} or higher.`);
      return;
    }
    if (
      payload.entries.length === 0
      || payload.entries.some(entry => (
        !entry.entryId
        || !entry.itemId
        || !entry.rarityId
        || entry.itemLevel < 1
        || entry.rollSeed < 0
        || entry.quantity < 1
      ))
    ) {
      setError('Complete every entry and use positive item levels and quantities.');
      return;
    }

    setSaving(true);
    try {
      const created = await createStarterKit(payload);
      setKits((current) => [created, ...current]
        .sort((left, right) => kitVersion(right) - kitVersion(left)));
      setSelectedKit(created);
    } catch (saveError) {
      setError(saveError?.message || 'The Starter Kit version could not be created.');
    } finally {
      setSaving(false);
    }
  };

  const itemName = (id) => {
    const item = items.find(candidate => (candidate.id ?? candidate.Id) === id);
    return item?.name ?? item?.Name ?? id;
  };
  const rarityName = (id) => {
    const rarity = rarities.find(candidate => (candidate.id ?? candidate.Id) === id);
    return rarity?.name ?? rarity?.Name ?? id;
  };

  return (
    <div className="admin-settingspage admin-starter-kits">
      <aside className="admin_list" aria-label="Starter Kit versions">
        <button className="button-primary" type="button" onClick={() => startNewVersion()}>
          Create version {nextVersion}
        </button>
        {loading && <p>Loading versions…</p>}
        {!loading && kits.length === 0 && <p>No released versions yet.</p>}
        {kits.map(kit => {
          const version = kitVersion(kit);
          const active = Boolean(read(kit, 'active', 'Active'));
          return (
            <button
              className={`listElement${kitId(selectedKit) === kitId(kit) ? ' is-active' : ''}`}
              key={kitId(kit) || version}
              type="button"
              onClick={() => selectKit(kit)}
            >
              Version {version}{active ? ' · active' : ''}
            </button>
          );
        })}
      </aside>

      <section className="admin_details">
        <header className="admin-starter-kits__header">
          <div>
            <h1>Starter Kits</h1>
            <p>Released versions are immutable. Create a higher version for every change.</p>
          </div>
          {selectedKit && (
            <button type="button" onClick={() => startNewVersion()}>
              Create next version
            </button>
          )}
        </header>

        {error && <p className="admin-changelog__error" role="alert">{error}</p>}
        {detailLoading && <p>Loading version…</p>}

        {!detailLoading && selectedKit ? (
          <div className="admin-starter-kits__released">
            <h2>Version {kitVersion(selectedKit)}</h2>
            <dl>
              <div><dt>Status</dt><dd>{read(selectedKit, 'active', 'Active') ? 'Active' : 'Inactive'}</dd></div>
              <div><dt>ID</dt><dd>{kitId(selectedKit)}</dd></div>
            </dl>
            <h3>Entries</h3>
            <div className="admin-starter-kits__entry-list">
              {kitEntries(selectedKit).map((entry, index) => {
                const entryId = read(entry, 'entryId', 'EntryId') || `Entry ${index + 1}`;
                const itemId = read(entry, 'itemId', 'ItemId') || '';
                const rarityId = read(entry, 'rarityId', 'RarityId') || '';
                return (
                  <article className="admin-starter-kits__entry" key={`${entryId}-${index}`}>
                    <h4>{entryId}</h4>
                    <p>{itemName(itemId)} · {rarityName(rarityId)}</p>
                    <p>
                      Level {read(entry, 'itemLevel', 'ItemLevel')}
                      {' · '}Quantity {read(entry, 'quantity', 'Quantity')}
                      {' · '}Seed {read(entry, 'rollSeed', 'RollSeed')}
                    </p>
                    <p>{read(entry, 'requiredToStart', 'RequiredToStart') ? 'Required to start' : 'Optional'}</p>
                  </article>
                );
              })}
            </div>
          </div>
        ) : !detailLoading && (
          <form className="admin-starter-kits__form" onSubmit={save}>
            <h2>Create immutable version</h2>
            <label>Version:
              <input
                type="number"
                min={nextVersion}
                step="1"
                value={draft.version}
                onChange={event => setDraft(current => ({
                  ...current,
                  version: Number.parseInt(event.target.value, 10) || 0,
                }))}
              />
            </label>
            <label>Active:
              <input checked disabled type="checkbox" />
              <small>A newly released version is always active.</small>
            </label>

            <div className="admin-starter-kits__entry-list">
              {draft.entries.map((entry, index) => (
                <fieldset className="admin-starter-kits__entry" key={index}>
                  <legend>Entry {index + 1}</legend>
                  <label>Entry ID:
                    <input
                      value={entry.entryId}
                      onChange={event => updateEntry(index, 'entryId', event.target.value)}
                      placeholder="weapon"
                    />
                  </label>
                  <label>Item:
                    <select
                      value={entry.itemId}
                      onChange={event => updateEntry(index, 'itemId', event.target.value)}
                    >
                      <option value="">Select an item</option>
                      {items.map(item => (
                        <option key={item.id ?? item.Id} value={item.id ?? item.Id}>
                          {item.name ?? item.Name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>Rarity:
                    <select
                      value={entry.rarityId}
                      onChange={event => updateEntry(index, 'rarityId', event.target.value)}
                    >
                      <option value="">Select a rarity</option>
                      {rarities.map(rarity => (
                        <option key={rarity.id ?? rarity.Id} value={rarity.id ?? rarity.Id}>
                          {rarity.name ?? rarity.Name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>Item level:
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={entry.itemLevel}
                      onChange={event => updateEntry(index, 'itemLevel', event.target.value)}
                    />
                  </label>
                  <label>Roll seed:
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={entry.rollSeed}
                      onChange={event => updateEntry(index, 'rollSeed', event.target.value)}
                    />
                  </label>
                  <label>Quantity:
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={entry.quantity}
                      onChange={event => updateEntry(index, 'quantity', event.target.value)}
                    />
                  </label>
                  <label>Required to start:
                    <input
                      type="checkbox"
                      checked={entry.requiredToStart}
                      onChange={event => updateEntry(index, 'requiredToStart', event.target.checked)}
                    />
                  </label>
                  <button
                    className="button-critical"
                    disabled={draft.entries.length === 1}
                    type="button"
                    onClick={() => removeEntry(index)}
                  >
                    Remove entry
                  </button>
                </fieldset>
              ))}
            </div>

            <div className="admin-starter-kits__actions">
              <button type="button" onClick={addEntry}>Add entry</button>
              <button className="button-primary" disabled={saving} type="submit">
                {saving ? 'Creating…' : `Create version ${draft.version}`}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
